import ModalViewModel from '../../../infra/ModalViewModel'
import { action, computed, makeObservable, observable, override } from 'mobx'
import { Id } from '../../../types/definitions'
import { SearchableUser } from '../../../iam/models/SearchableUser'
import Workspace from '../../../iam/models/Workspace'
import MemberStore from '../../../iam/MemberStore'
import { AlertFactory, AlertState } from '../../../alerts/Alert'
import { task } from 'mobx-task'
import { pluralize } from '../../../utils/pluralization-util'

export class RestoreToWorkspaceViewModel extends ModalViewModel {
  @observable
  private _selectedUserIds: Set<Id> = new Set()

  @observable
  selectedUser?: SearchableUser = SearchableUser.none

  @observable
  workspace: Workspace = Workspace.none

  constructor(
    private memberStore: MemberStore,
    private newAlert: AlertFactory,
    private removeUsers?: (userIds: Array<Id>) => void,
  ) {
    super()
    makeObservable(this)
  }

  @action
  setSelectedUser(user: SearchableUser) {
    this.selectedUser = user
    this._selectedUserIds = new Set([user.publicId])
  }

  @computed
  get selectedUserIds() {
    return Array.from(this._selectedUserIds)
  }

  @action
  setSelectedUserIds(selectedUserIds: Set<Id>) {
    this._selectedUserIds = selectedUserIds
  }

  @action
  setSelectedWorkspace(workspace: Workspace) {
    this.workspace = workspace
  }

  showWithUser(user: SearchableUser, workspace: Workspace) {
    this.setSelectedUser(user)
    this.setSelectedWorkspace(workspace)
    super.show()
  }

  open(selectedUserIds: Set<Id>, workspace: Workspace) {
    this.setSelectedUserIds(selectedUserIds)
    this.setSelectedWorkspace(workspace)
    this.show()
  }

  restore = task.resolved(async () => {
    const alert = this.newAlert()

    alert
      .withState(AlertState.Pending)
      .saying(`Restoring ${pluralize('User', this.selectedUserIds.length)}!`)
      .autoDismiss()
      .show()

    try {
      await Promise.all(
        this.selectedUserIds.map((userId) =>
          this.memberStore.update(
            {
              user_public_id: Id.assert(userId),
              workspace_id: Id.assert(this.workspace.id),
            },
            { inactive: false },
          ),
        ),
      )
      this.removeUsers?.(this.selectedUserIds)

      alert
        .withState(AlertState.Success)
        .saying(
          'Success!',
          `${pluralize('User', this.selectedUserIds.length)} restored successfully!`,
        )
        .autoDismiss()
        .show()

      this.close()
    } catch (e) {
      console.error(e)

      alert.fromError(e).autoDismiss()
    }
  })

  @action
  clear() {
    this.workspace = Workspace.none
    this.selectedUser = SearchableUser.none
  }

  @override
  close() {
    this.clear()
    super.close()
  }
}
